// recipient.ts -- shared client-side helpers for the Recipient
// discriminated union.
//
// Two responsibilities:
//   1. recipientKey()  -- canonical denormalized join key, mirrors the
//      server's recipientKeyFor() so URL params + indexes line up.
//   2. recipientForWire() -- strips UI-only fields (name, email,
//      avatarEmoji) so the BFF receives a schema-pure object. Per the
//      F.9 strict-contracts rule: NEVER spread the full UI Recipient
//      into a wire payload; always project it through this helper.
//
// Pattern used at every send site (chatStore.send, openWindow's resume
// path, etc.) so the wire shape is constructed deliberately, not
// accidentally inherited.
/**
 * Canonical denormalized join key for a recipient. Used in:
 *   - GET /sessions/by-recipient/:key URLs
 *   - the by-recipient index path in fdn_chat_sessions
 *   - the client's "is there already an open window with this
 *     recipient?" lookup in openWindow's de-dup path
 *
 * MUST match the server's recipientKeyFor() in chat.ts. If you add
 * a new Recipient kind, update both.
 */
export function recipientKey(r) {
    switch (r.kind) {
        case 'fdn': return 'fdn';
        case 'coworker': return `coworker:${r.coworkerId}`;
        case 'user': return `user:${r.userId}`;
    }
}
/**
 * Project a UI Recipient down to its schema-pure wire shape. Use this
 * EVERY time a Recipient is included in an HTTP request body. Do NOT
 * `...spread` a UI Recipient into a request payload -- that lets
 * UI-only fields leak through.
 */
export function recipientForWire(r) {
    switch (r.kind) {
        case 'fdn': return { kind: 'fdn' };
        case 'coworker': return { kind: 'coworker', coworkerId: r.coworkerId };
        case 'user': return { kind: 'user', userId: r.userId };
    }
}
